// Display · ProgressBar
//
// Linear progress indicator. Thin wrap around AntD `Progress` with our
// shared `Tone` vocabulary and a closed-enum `size` scale. The trail
// (unfilled track) is always the neutral hairline color.

import { Progress } from "antd";
import {
  DOMAIN,
  SURFACE,
  TEXT,
  TONE,
  type ToneName,
} from "@ds/tokens/palette";

export type ProgressBarSize = "xs" | "sm" | "md" | "lg";

export type ProgressBarTone =
  | "neutral"
  | "primary"
  | "muted"
  | "revenue"
  | "cost"
  | "correction"
  | ToneName;

export interface ProgressBarInput {
  /** Fill percent (clamped to 0..100). */
  percent: number;
  /** Color tone of the filled portion. Default `"revenue"` (green). */
  tone?: ProgressBarTone;
  /** Height scale: xs=2, sm=4, md=6, lg=10 px. Default `"sm"`. */
  size?: ProgressBarSize;
  /** Show numeric percent label after the bar. */
  showPercent?: boolean;
  /** `data-testid` on the AntD Progress root. */
  testId?: string;
  className?: string;
}

export type ProgressBarProps = ProgressBarInput;

// ─── Token resolvers ──────────────────────────────────────────────────────

const SIZE_TO_PX: Record<ProgressBarSize, number> = {
  xs: 2,
  sm: 4,
  md: 6,
  lg: 10,
};

function resolveStroke(tone: ProgressBarTone): string {
  switch (tone) {
    case "neutral":    return TEXT.muted;
    case "primary":    return TEXT.primary;
    case "muted":      return TEXT.muted;
    case "revenue":    return DOMAIN.revenue;
    case "cost":       return DOMAIN.cost;
    case "correction": return DOMAIN.correction;
    default:           return TONE[tone].base;
  }
}

// ─── Component ────────────────────────────────────────────────────────────

export function ProgressBar({
  percent,
  tone = "revenue",
  size = "sm",
  showPercent = false,
  testId,
  className,
}: ProgressBarProps) {
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div data-testid={testId} className={className}>
      <Progress
        percent={clamped}
        size={{ height: SIZE_TO_PX[size] }}
        showInfo={showPercent}
        strokeColor={resolveStroke(tone)}
        trailColor={SURFACE.borderSoft}
      />
    </div>
  );
}
